import React, { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Building2, Image as ImageIcon, FolderCog, FileText, ShieldCheck, Save, Check, X, PenTool, Upload, BadgeCheck } from "lucide-react";
import { toast } from "sonner";

const SIGN_TYPES = ["official_signature", "official_stamp", "director_signature", "finance_signature", "administration_signature"];

export default function Settings() {
  const { t } = useI18n();
  const { hasRole } = useAuth();
  const [settings, setSettings] = useState(null);
  const [branding, setBranding] = useState([]);
  const [cats, setCats] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [roles, setRoles] = useState([]);
  const [access, setAccess] = useState({});
  const [signatures, setSignatures] = useState([]);

  const loadSignatures = () => api.get("/branding-assets").then((r) => setSignatures(r.data.filter((a) => SIGN_TYPES.includes(a.asset_type)))).catch(() => {});

  useEffect(() => {
    api.get("/settings/access").then((r) => setAccess(r.data.tabs || {})).catch(() => {});
    api.get("/academy-settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/branding-assets").then((r) => setBranding(r.data)).catch(() => {});
    api.get("/document-categories").then((r) => setCats(r.data)).catch(() => {});
    api.get("/pdf-templates").then((r) => setTemplates(r.data)).catch(() => {});
    api.get("/roles").then((r) => setRoles(r.data)).catch(() => {});
    loadSignatures();
  }, []);

  const can = (tab, lvl = "view") => {
    const order = { none: 0, view: 1, edit: 2 };
    return (order[access[tab]] || 0) >= order[lvl];
  };
  const canEdit = can("academy", "edit");

  const saveSettings = async () => {
    try {
      const { official_name, app_name, official_email, phone, address, website, currency } = settings;
      await api.put("/academy-settings", { official_name, app_name, official_email, phone, address, website, currency });
      toast.success(t("save"));
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const bool = (v) => v ? <Check className="h-4 w-4 text-emerald-600" /> : <X className="h-4 w-4 text-slate-300" />;

  return (
    <div>
      <h1 className="text-2xl font-display font-bold text-slate-900 mb-1">{t("settings")}</h1>
      <p className="text-sm text-slate-500 mb-6">{t("appName")} — fondations & paramètres</p>

      <Tabs defaultValue="academy">
        <TabsList className="mb-4 flex-wrap h-auto">
          {can("academy") && <TabsTrigger value="academy" data-testid="tab-academy"><Building2 className="h-4 w-4 mr-1.5" />{t("academySettings")}</TabsTrigger>}
          {can("branding") && <TabsTrigger value="branding" data-testid="tab-branding"><ImageIcon className="h-4 w-4 mr-1.5" />{t("brandingAssets")}</TabsTrigger>}
          {can("signatures") && <TabsTrigger value="signatures" data-testid="tab-signatures"><PenTool className="h-4 w-4 mr-1.5" />{t("signaturesTab")}</TabsTrigger>}
          {can("categories") && <TabsTrigger value="categories" data-testid="tab-categories"><FolderCog className="h-4 w-4 mr-1.5" />{t("categories")}</TabsTrigger>}
          {can("templates") && <TabsTrigger value="templates" data-testid="tab-templates"><FileText className="h-4 w-4 mr-1.5" />{t("pdfTemplates")}</TabsTrigger>}
          {can("roles") && <TabsTrigger value="roles" data-testid="tab-roles"><ShieldCheck className="h-4 w-4 mr-1.5" />{t("rolesAndPermissions")}</TabsTrigger>}
          {hasRole("super_admin", "direction", "administration") && <TabsTrigger value="admissions" data-testid="tab-admissions-settings"><FileText className="h-4 w-4 mr-1.5" />{t("admissions")}</TabsTrigger>}
        </TabsList>

        {hasRole("super_admin", "direction", "administration") && (
          <TabsContent value="admissions">
            <AdmissionsSettingsPanel canEdit={hasRole("super_admin", "direction")} />
          </TabsContent>
        )}

        <TabsContent value="academy">
          {settings && (
            <Card className="card-float border-slate-200 p-6 max-w-2xl">
              <div className="grid sm:grid-cols-2 gap-4">
                {[["official_name", t("officialName")], ["app_name", t("appName")], ["official_email", t("email")],
                  ["phone", t("phone")], ["address", t("address")], ["website", "Site web"], ["currency", "Devise"]].map(([k, label]) => (
                  <div key={k} className="grid gap-1.5">
                    <label className="text-sm font-medium text-slate-600">{label}</label>
                    <Input data-testid={`set-${k}`} value={settings[k] || ""} disabled={!canEdit}
                      onChange={(e) => setSettings({ ...settings, [k]: e.target.value })} />
                  </div>
                ))}
              </div>
              <div className="mt-4 flex items-center gap-2 flex-wrap">
                <span className="text-sm text-slate-500 mr-2">{t("languages")}:</span>
                {(settings.available_languages || []).map((l) => <Badge key={l} variant="secondary">{l}</Badge>)}
              </div>
              {canEdit && <Button data-testid="settings-save-btn" onClick={saveSettings} className="mt-5 rounded-full gap-2"><Save className="h-4 w-4" />{t("save")}</Button>}
            </Card>
          )}
        </TabsContent>

        <TabsContent value="branding">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {branding.map((b) => (
              <Card key={b.id} className="card-float border-slate-200 p-4">
                <div className="h-24 rounded-lg bg-slate-50 border border-slate-100 flex items-center justify-center overflow-hidden mb-3">
                  <img src={b.file_url} alt={b.asset_name} className="max-h-full max-w-full object-contain" />
                </div>
                <div className="font-medium text-slate-800 text-sm">{b.asset_name}</div>
                <div className="text-xs text-slate-500">{b.asset_type} · {b.usage_context}</div>
              </Card>
            ))}
          </div>
        </TabsContent>

        {can("signatures") && (
          <TabsContent value="signatures">
            <SignaturesPanel assets={signatures} canEdit={can("signatures", "edit")} onChange={loadSignatures} />
          </TabsContent>
        )}

        <TabsContent value="categories">
          <Card className="card-float border-slate-200 overflow-hidden">
            <Table>
              <TableHeader><TableRow className="bg-slate-50/70 hover:bg-slate-50/70">
                <TableHead>{t("name")}</TableHead><TableHead>Module</TableHead><TableHead>{t("sensitiveDoc")}</TableHead>
                <TableHead>Filigrane</TableHead><TableHead>Scan papier</TableHead><TableHead>Confidentialité</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {cats.map((c) => (
                  <TableRow key={c.id} data-testid="row-category">
                    <TableCell className="font-medium text-slate-800">{c.name}</TableCell>
                    <TableCell className="text-slate-500">{c.module}</TableCell>
                    <TableCell>{bool(c.is_sensitive_by_default)}</TableCell>
                    <TableCell>{bool(c.watermark_required_by_default)}</TableCell>
                    <TableCell>{bool(c.physical_scan_required_by_default)}</TableCell>
                    <TableCell><Badge variant="secondary" className="text-[10px]">{c.default_confidentiality_level}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="templates">
          <Card className="card-float border-slate-200 overflow-hidden">
            <Table>
              <TableHeader><TableRow className="bg-slate-50/70 hover:bg-slate-50/70">
                <TableHead>{t("title")}</TableHead><TableHead>Module</TableHead><TableHead>Logo Académie</TableHead>
                <TableHead>Logo InZeAir</TableHead><TableHead>Signature</TableHead><TableHead>Officiel</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {templates.map((tp) => (
                  <TableRow key={tp.id} data-testid="row-template">
                    <TableCell className="font-medium text-slate-800">{tp.title}</TableCell>
                    <TableCell className="text-slate-500">{tp.module}</TableCell>
                    <TableCell>{bool(tp.uses_academy_logo)}</TableCell>
                    <TableCell>{bool(tp.uses_inzeair_logo)}</TableCell>
                    <TableCell>{bool(tp.includes_signature)}</TableCell>
                    <TableCell>{bool(tp.is_official_document)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="roles">
          <div className="grid sm:grid-cols-2 gap-4">
            {roles.map((r) => (
              <Card key={r.id} className="card-float border-slate-200 p-4" data-testid="row-role">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-display font-semibold text-slate-800">{r.label_fr}</div>
                  <Badge variant="secondary" className="text-[10px]">{r.code}</Badge>
                </div>
                <div className="flex flex-wrap gap-1">
                  {(r.permissions || []).map((p) => <Badge key={p} className="bg-sky-50 text-primary border-0 text-[10px]">{p}</Badge>)}
                </div>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function AdmissionsSettingsPanel({ canEdit }) {
  const { t } = useI18n();
  const [val, setVal] = useState(7);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admissions/settings").then((r) => setVal(r.data.blocked_after_days)).catch(() => {}); }, []);
  const save = async () => {
    setBusy(true);
    try { await api.put("/admissions/settings", { blocked_after_days: Number(val) }); toast.success(t("save")); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Card className="card-float border-slate-200 p-6 max-w-lg">
      <h3 className="font-display font-semibold text-slate-800 mb-1">{t("admissions")}</h3>
      <div className="grid gap-1.5 mt-3">
        <label className="text-sm font-medium text-slate-600">{t("blockedAfterDays")}</label>
        <Input type="number" min={1} max={60} data-testid="blocked-after-days-input" value={val} disabled={!canEdit}
          onChange={(e) => setVal(e.target.value)} className="max-w-[140px]" />
        <p className="text-xs text-slate-500">{t("blockedAfterDaysHelp")}</p>
      </div>
      {canEdit && <Button data-testid="blocked-after-days-save" onClick={save} disabled={busy} className="mt-4 rounded-full gap-2"><Save className="h-4 w-4" />{t("save")}</Button>}
    </Card>
  );
}

function SignaturesPanel({ assets, canEdit, onChange }) {
  const { t } = useI18n();
  const fileRef = React.useRef(null);
  const [form, setForm] = useState({ asset_type: "official_signature", asset_name: "", allowed_template_types: "" });
  const [busy, setBusy] = useState(false);

  const upload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error("Sélectionnez un fichier"); return; }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("asset_type", form.asset_type);
    fd.append("asset_name", form.asset_name || form.asset_type);
    fd.append("usage_context", "pdf");
    fd.append("allowed_template_types", form.allowed_template_types);
    setBusy(true);
    try {
      await api.post("/branding-assets/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("save")); if (fileRef.current) fileRef.current.value = ""; setForm({ ...form, asset_name: "" }); onChange();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const validate = async (id) => { try { await api.put(`/branding-assets/${id}/validate`); toast.success(t("validated")); onChange(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };

  return (
    <div className="grid lg:grid-cols-3 gap-4">
      {canEdit && (
        <Card className="card-float border-slate-200 p-5">
          <h3 className="font-display font-semibold text-slate-800 mb-3">{t("uploadAsset")}</h3>
          <div className="grid gap-3">
            <div className="grid gap-1.5"><label className="text-sm text-slate-600">{t("assetType")}</label>
              <Select value={form.asset_type} onValueChange={(v) => setForm({ ...form, asset_type: v })}>
                <SelectTrigger data-testid="sig-type"><SelectValue /></SelectTrigger>
                <SelectContent>{SIGN_TYPES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="grid gap-1.5"><label className="text-sm text-slate-600">{t("name")}</label>
              <Input data-testid="sig-name" value={form.asset_name} onChange={(e) => setForm({ ...form, asset_name: e.target.value })} /></div>
            <div className="grid gap-1.5"><label className="text-sm text-slate-600">{t("allowedTemplates")}</label>
              <Input value={form.allowed_template_types} onChange={(e) => setForm({ ...form, allowed_template_types: e.target.value })} placeholder="contract,invoice,bulletin" /></div>
            <div className="grid gap-1.5"><label className="text-sm text-slate-600">Fichier (PNG transparent)</label>
              <Input data-testid="sig-file" ref={fileRef} type="file" accept=".png,.jpg,.jpeg,.webp" /></div>
            <Button data-testid="sig-upload-btn" onClick={upload} disabled={busy} className="rounded-full gap-2"><Upload className="h-4 w-4" />{t("uploadAsset")}</Button>
          </div>
        </Card>
      )}
      <div className="lg:col-span-2 grid sm:grid-cols-2 gap-3">
        {assets.length === 0 ? <Card className="card-float border-slate-200 p-8 text-center text-slate-400">{t("noData")}</Card> :
          assets.map((a) => (
            <Card key={a.id} data-testid="row-signature" className="card-float border-slate-200 p-4">
              <div className="h-20 rounded-lg bg-slate-50 border flex items-center justify-center overflow-hidden mb-2">
                <img src={`${process.env.REACT_APP_BACKEND_URL}/api/branding-assets/${a.id}/file`} alt={a.asset_name} className="max-h-full object-contain" />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-slate-800">{a.asset_name}</div>
                  <div className="text-xs text-slate-500">{a.asset_type}{!a.is_active && " · inactif"}</div>
                </div>
                {a.validated_at
                  ? <Badge className="bg-emerald-100 text-emerald-700 border-0 gap-1"><BadgeCheck className="h-3 w-3" />{t("validated")}</Badge>
                  : canEdit && <Button data-testid="sig-validate-btn" size="sm" variant="outline" onClick={() => validate(a.id)}>{t("validate")}</Button>}
              </div>
            </Card>
          ))}
      </div>
    </div>
  );
}
